import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Snackbar, SnackbarContent } from '@mui/material';
import {
  detectDirectoryInDataTransferBestEffort,
  detectDirectoryInDropEvent,
} from 'helpers/externalDragAndDrop';
import { DragDropVariant, resolveDropzoneSx } from './styles';
import { Color } from 'shared/utils/colors';

interface Props {
  handleInput: Function;
  children: React.ReactNode;
  'data-qa': string;
  variant?: DragDropVariant;
  snackbarMessage?: React.ReactNode;
  onDirectoryDropRejected?: () => void;
}

const DragAndDrop: React.FC<Props> = ({
  handleInput,
  children,
  ['data-qa']: dataQa,
  variant = 'default',
  snackbarMessage,
  onDirectoryDropRejected,
}) => {
  const [isDraggingFilesOver, setIsDraggingFilesOver] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const nestedDragEventsCounter = useRef(0);

  const isFileDragEvent = (event: DragEvent): boolean => {
    return event.dataTransfer?.types?.includes('Files') ?? false;
  };

  const hasLeftEntireDropzone = (): boolean => {
    return nestedDragEventsCounter.current === 0;
  };

  const extractFilesFromEvent = (event: DragEvent): File[] => {
    return Array.from(event.dataTransfer?.files || []);
  };

  const updateDragHighlight = useCallback((event: DragEvent): void => {
    if (!isFileDragEvent(event)) {
      return;
    }

    const detection = detectDirectoryInDataTransferBestEffort(
      event.dataTransfer
    );

    if (detection === true) {
      setIsDraggingFilesOver(false);
      return;
    }

    setIsDraggingFilesOver(true);
  }, []);

  const onDragEnterNestedElement = useCallback(
    (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      nestedDragEventsCounter.current++;

      updateDragHighlight(e);
    },
    [updateDragHighlight]
  );

  const onDragOverDropzone = useCallback((e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();

    const detection = detectDirectoryInDataTransferBestEffort(
      e.dataTransfer
    );
    if (detection === true && e.dataTransfer) {
      e.dataTransfer.dropEffect = 'none';
    }
  }, []);

  const onDragLeaveNestedElement = useCallback((e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();

    nestedDragEventsCounter.current--;

    if (hasLeftEntireDropzone()) {
      setIsDraggingFilesOver(false);
    }
  }, []);

  const onDropFiles = useCallback(
    async (e: DragEvent): Promise<void> => {
      e.preventDefault();
      e.stopPropagation();

      nestedDragEventsCounter.current = 0;
      setIsDraggingFilesOver(false);

      if (await detectDirectoryInDropEvent(e)) {
        onDirectoryDropRejected?.();
        return;
      }

      const files = extractFilesFromEvent(e);
      if (files.length > 0) {
        handleInput(files);
      }
    },
    [handleInput, onDirectoryDropRejected]
  );

  useEffect((): (() => void) => {
    const dropzoneElement = ref.current;
    if (dropzoneElement) {
      dropzoneElement.addEventListener('dragenter', onDragEnterNestedElement);
      dropzoneElement.addEventListener('dragleave', onDragLeaveNestedElement);
      dropzoneElement.addEventListener('drop', onDropFiles);
      dropzoneElement.addEventListener('dragover', onDragOverDropzone);
    }

    return (): void => {
      if (dropzoneElement) {
        dropzoneElement.removeEventListener(
          'dragenter',
          onDragEnterNestedElement
        );
        dropzoneElement.removeEventListener(
          'dragleave',
          onDragLeaveNestedElement
        );
        dropzoneElement.removeEventListener('drop', onDropFiles);
        dropzoneElement.removeEventListener('dragover', onDragOverDropzone);
      }
    };
  });

  return (
    <>
      <Box
        sx={resolveDropzoneSx(variant, isDraggingFilesOver)}
        ref={ref}
        data-qa={dataQa}
      >
        {children}
      </Box>
      {snackbarMessage && (
        <Snackbar
          open={isDraggingFilesOver}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ zIndex: 30000 }}
        >
          <SnackbarContent
            sx={{
              backgroundColor: Color.primaryOrange,
              borderRadius: 1,
              color: Color.black,
            }}
            message={snackbarMessage}
          />
        </Snackbar>
      )}
    </>
  );
};

export default DragAndDrop;
